// Words for QRISK3 10-year cardiovascular risk (TGCLOUD-1911): the calculation
// dialog, the stored result, the risk records lists, the Clinical Records tiles
// and the summary PDF. Every QRISK3 name and notice the screens show is here,
// so the naming decision on the ticket (open question 3, the QRISK trademark)
// is one file's change. Pure; `t` defaults to English.
import { english } from 'src/i18n/language';
import { SMOKING_OPTIONS } from './measurementsText';

// The algorithm, named to identify it: a clinician must know which model
// produced a number, and the mandatory disclaimer names it anyway.
export const MODEL_NAME = 'QRISK3-2017';
// What the feature does, which is how the screens title it.
export const FEATURE_TITLE = '10-year cardiovascular risk';

// ClinRisk Ltd's disclaimer, VERBATIM from the "Additional terms" of the
// QRISK3-2017 source (every redistribution checked carries it word for word;
// docs/qrisk3/phase-0-findings.md §1). The licence requires it to be "held
// together with any risk score" and displayed with a displayed score. It is
// shown as written, never translated: a translation would no longer be
// ClinRisk's words. Bound by qrisk3Text.test.js to the source the backend runs.
export const CLINRISK_DISCLAIMER = [
  'The initial version of this file, to be found at http://svn.clinrisk.co.uk/opensource/qrisk2, faithfully implements QRISK3-2017.',
  'ClinRisk Ltd. have released this code under the GNU Lesser General Public License to enable others to implement the algorithm faithfully.',
  'However, the nature of the GNU Lesser General Public License is such that we cannot prevent, for example, someone accidentally altering the coefficients, getting the inputs wrong, or just poor programming.',
  'ClinRisk Ltd. stress, therefore, that it is the responsibility of the end user to check that the source that they receive produces the same results as the original code found at https://qrisk.org.',
  'Inaccurate implementations of risk scores can lead to wrong patients being given the wrong treatment.'
].join(' ');

// As the ticket quotes it.
export const TRADEMARK_NOTICE =
  'QRISK® is a trademark jointly held by the University of Nottingham and EMIS.';

// TrakGene's own words about the licence; translated where shown.
export const LICENCE_NOTICE =
  'QRISK3-2017 is copyright 2017 ClinRisk Ltd. It is free software, released under the GNU Lesser General Public License, version 3 or later, and TrakGene uses it unmodified. TrakGene checks that it gives the same results as ClinRisk’s original source.';

export const ETHNICITY_OPTIONS = [
  { value: 1, label: 'White or not stated' },
  { value: 2, label: 'Indian' },
  { value: 3, label: 'Pakistani' },
  { value: 4, label: 'Bangladeshi' },
  { value: 5, label: 'Other Asian' },
  { value: 6, label: 'Black Caribbean' },
  { value: 7, label: 'Black African' },
  { value: 8, label: 'Chinese' },
  { value: 9, label: 'Other ethnic group' }
];

// QRISK3's smoking categories by the code the algorithm takes (0-4), worded as
// the measurement form words them.
export const SMOKING_CODE_OPTIONS = SMOKING_OPTIONS.map((o, code) => ({
  value: code,
  label: o.label,
  status: o.value
}));

export const DIABETES_OPTIONS = [
  { value: 'none', label: 'No diabetes' },
  { value: 'type1', label: 'Type 1 diabetes' },
  { value: 'type2', label: 'Type 2 diabetes' }
];

// The yes/no conditions, in QRISK3's order, with the paper's definitions
// (Hippisley-Cox J, Coupland C, Brindle P. BMJ 2017;357:j2099, CC BY 4.0).
export const CONDITION_FIELDS = [
  {
    key: 'af',
    label: 'Atrial fibrillation',
    definition:
      'Including atrial fibrillation, atrial flutter, and paroxysmal atrial fibrillation.'
  },
  {
    key: 'rheumatoidArthritis',
    label: 'Rheumatoid arthritis',
    definition:
      "Rheumatoid arthritis, Felty's syndrome, Caplan's syndrome, adult onset Still's disease, or inflammatory polyarthropathy not otherwise specified."
  },
  {
    key: 'ckd345',
    label: 'Chronic kidney disease (stage 3, 4 or 5)',
    definition:
      'Stage 3, 4 or 5, and major chronic renal disease (including nephrotic syndrome, chronic glomerulonephritis, chronic pyelonephritis, renal dialysis, and renal transplant).'
  },
  {
    key: 'migraine',
    label: 'Migraine',
    definition:
      'Including classic, atypical, abdominal, basilar and hemiplegic migraine, migraine with or without aura, and cluster headaches.'
  },
  {
    key: 'sle',
    label: 'Systemic lupus erythematosus (SLE)',
    definition:
      'Including SLE, disseminated lupus erythematosus, or Libman-Sacks disease.'
  },
  {
    key: 'severeMentalIllness',
    label: 'Severe mental illness',
    definition:
      'Including psychosis, schizophrenia, or bipolar affective disease.'
  },
  {
    key: 'treatedHypertension',
    label: 'On blood pressure treatment',
    definition:
      'A diagnosis of hypertension and treatment with at least one antihypertensive drug.'
  },
  {
    key: 'corticosteroids',
    label: 'On regular corticosteroid tablets',
    definition:
      'Current use: at least two prescriptions, the latest within the last 28 days (oral or parenteral prednisolone, betamethasone, cortisone, dexamethasone, hydrocortisone and others in BNF 6.3.2).'
  },
  {
    key: 'atypicalAntipsychotics',
    label: 'On atypical antipsychotic medication',
    definition:
      'Second generation antipsychotics, such as amisulpride, aripiprazole, clozapine, lurasidone, olanzapine, paliperidone, quetiapine, risperidone, sertindole or zotepine.'
  },
  {
    key: 'erectileDysfunction',
    label: 'Erectile dysfunction',
    definition: 'A diagnosis of, or treatment for, erectile dysfunction.',
    maleOnly: true
  }
];

export const INPUT_LABELS = {
  sex: 'Sex at birth',
  age: 'Age (years)',
  ethnicity: 'Ethnicity',
  heightCm: 'Height (cm)',
  weightKg: 'Weight (kg)',
  bmi: 'BMI',
  smoking: 'Smoking',
  systolicBp: 'Systolic blood pressure (mmHg)',
  // Short, so it sits on one line beside the blood pressure it describes.
  systolicBpSd: 'Systolic blood pressure SD (mmHg)',
  cholesterolHdlRatio: 'Total cholesterol to HDL ratio',
  townsend: 'Townsend deprivation score',
  familyHistoryChd:
    'Angina or heart attack in a first-degree relative under 60',
  diabetes: 'Diabetes',
  confirmedNoCvdNoStatin: 'Applies to this person',
  ...Object.fromEntries(CONDITION_FIELDS.map((c) => [c.key, c.label]))
};

const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label;

/** A stored input's value, worded. */
export function inputValueText(field, value, t = english) {
  if (value === null || value === undefined || value === '') return '';
  switch (field) {
    case 'sex':
      return value === 'male'
        ? t('Male')
        : value === 'female'
        ? t('Female')
        : '';
    case 'ethnicity':
      return t(optionLabel(ETHNICITY_OPTIONS, value) || '');
    case 'smoking':
      return t(optionLabel(SMOKING_CODE_OPTIONS, value) || '');
    case 'diabetes':
      return t(optionLabel(DIABETES_OPTIONS, value) || '');
    case 'bmi':
      return Number(value).toFixed(1);
    default:
      if (typeof value === 'boolean') return value ? t('Yes') : t('No');
      return String(value);
  }
}

/**
 * Where an input came from, as the prefill or a stored result says it.
 * @param {object} src  { source, measuredOn, readings, from, to, … }
 * @param {Function} fmtDate  useDateFormatter's formatter
 */
export function sourceText(src, t = english, fmtDate = (d) => d) {
  switch (src?.source) {
    case 'measurement':
      return src.enteredHere
        ? t('Entered here, recorded as measured on {{date}}', {
            date: fmtDate(src.measuredOn)
          })
        : t('Measured {{date}}', { date: fmtDate(src.measuredOn) });
    case 'questionnaire':
      return src.cigarettesPerDay
        ? t('From the family history questionnaire: {{count}} a day', {
            count: src.cigarettesPerDay
          })
        : t('From the family history questionnaire');
    case 'date-of-birth':
      return t('From the date of birth');
    case 'recorded-age':
      return t('The age recorded on the pedigree');
    case 'record':
      return t('From the record');
    case 'not-recorded':
      return t('Not recorded');
    case 'pedigree':
      return t('From the pedigree');
    case 'diagnosis':
      return t('From recorded diagnoses');
    case 'readings':
      return t('From {{count}} readings, {{from}} to {{to}}', {
        count: src.readings,
        from: fmtDate(src.from),
        to: fmtDate(src.to)
      });
    case 'height-and-weight':
      return t('From height and weight');
    case 'clinician':
      return t('Entered');
    case 'assumed':
      return t('Assumed');
    default:
      return '';
  }
}

/** The words for one problem a calculation was refused for. */
export function qrisk3ProblemText(problem, t = english) {
  const r = (min, max) => ({ min, max, value: problem?.value });
  switch (problem?.code) {
    case 'SEX_REQUIRED':
      return t(
        'Choose the sex at birth: QRISK3 has separate equations for men and women.'
      );
    case 'AGE_REQUIRED':
      return t('Enter the age.');
    case 'AGE_NOT_WHOLE_YEARS':
      return t('Enter the age in whole years.');
    case 'AGE_OUT_OF_RANGE':
      return t(
        'QRISK3 is for people aged {{min}} to {{max}}.',
        r(problem.min, problem.max)
      );
    case 'ETHNICITY_REQUIRED':
      return t('Choose an ethnicity.');
    case 'BMI_REQUIRED':
      return t('Enter height and weight, or a BMI.');
    case 'BMI_OUT_OF_RANGE':
      return t(
        'A BMI of {{value}} is outside the range QRISK3 accepts ({{min}} to {{max}}).',
        r(problem.min, problem.max)
      );
    case 'SMOKING_REQUIRED':
      return t('Choose a smoking category.');
    case 'SBP_REQUIRED':
      return t('Enter the systolic blood pressure.');
    case 'SBP_OUT_OF_RANGE':
      return t(
        'A systolic blood pressure of {{value}} is outside the range QRISK3 accepts ({{min}} to {{max}} mmHg).',
        r(problem.min, problem.max)
      );
    case 'SBP_SD_REQUIRED':
      return t(
        'Enter the standard deviation of systolic blood pressure, or record at least two readings from the last five years.'
      );
    case 'SBP_SD_OUT_OF_RANGE':
      return t(
        'A standard deviation of {{value}} is outside the range QRISK3 accepts ({{min}} to {{max}} mmHg).',
        r(problem.min, problem.max)
      );
    case 'RATIO_REQUIRED':
      return t('Enter the total cholesterol to HDL ratio.');
    case 'RATIO_OUT_OF_RANGE':
      return t(
        'A cholesterol ratio of {{value}} is outside the range QRISK3 accepts ({{min}} to {{max}}).',
        r(problem.min, problem.max)
      );
    case 'TOWNSEND_REQUIRED':
      return t(
        'Enter a Townsend deprivation score, or tick that it is not known.'
      );
    case 'TOWNSEND_OUT_OF_RANGE':
      return t(
        'A Townsend score of {{value}} is outside the range QRISK3 accepts ({{min}} to {{max}}).',
        r(problem.min, problem.max)
      );
    case 'FAMILY_HISTORY_REQUIRED':
      return t(
        'Answer whether a first-degree relative had angina or a heart attack under 60.'
      );
    case 'ANSWER_REQUIRED':
      return t('Answer yes or no for every condition.');
    case 'DIABETES_REQUIRED':
      return t('Choose the diabetes status.');
    case 'APPLICABILITY_NOT_CONFIRMED':
      return t(
        'Confirm that this person has no cardiovascular disease and is not taking a statin.'
      );
    case 'CVD_RECORDED':
      return t(
        'QRISK3 does not apply: cardiovascular disease (coronary heart disease, stroke or TIA) is recorded for this person.'
      );
    case 'DECEASED':
      return t('QRISK3 does not apply: this person is recorded as deceased.');
    default:
      return t('The risk could not be calculated.');
  }
}

/** A data note shown with the inputs or the result. */
export function noteText(code, t = english) {
  switch (code) {
    case 'TOWNSEND_NOT_KNOWN':
      return t(
        'The Townsend deprivation score was not known, so 0 (the national average) was used.'
      );
    case 'DOB_YEAR_ONLY':
      return t(
        'Only the year of birth is recorded, so the age may be a year out.'
      );
    case 'DOB_MONTH_ONLY':
      return t(
        'Only the month of birth is recorded, so the age may be a year out.'
      );
    case 'DOB_ESTIMATED':
      return t('The date of birth is an estimate.');
    case 'AGE_AS_RECORDED':
      return t(
        'There is no date of birth; the age recorded on the pedigree was used.'
      );
    case 'NO_SAVED_PEDIGREE':
      return t(
        'This family has no saved pedigree, so relatives were read from their relationship labels.'
      );
    case 'INDEX_NOT_ON_PEDIGREE':
      return t(
        'This person is not on the pedigree, so their relatives could not be read.'
      );
    case 'MEMBERS_NOT_ON_PEDIGREE':
      return t(
        'Some family members are not connected on the pedigree and were not read.'
      );
    case 'NO_FIRST_DEGREE_RELATIVES':
      return t('No parent, brother, sister or child is recorded.');
    case 'HIDDEN_RELATIVE_NOT_COUNTED':
      return t(
        'A relative hidden from the family has heart disease recorded, which was not counted.'
      );
    case 'CURRENT_SMOKER_AMOUNT_NOT_KNOWN':
      return t('The questionnaire says this person smokes, but not how much.');
    case 'SEX_AT_BIRTH_NOT_RECORDED':
      return t('Sex at birth is not recorded.');
    case 'BOTH_TYPES_RECORDED':
      return t('Both type 1 and type 2 diabetes are recorded.');
    default:
      return '';
  }
}

/** Why a recorded entry did not count, beside it. */
export function entryReasonText(reason, t = english) {
  switch (reason) {
    case 'other-disease':
      return t("the patient's own words");
    case 'code-not-in-set':
      return t('its code is not one QRISK3 counts');
    case 'snomed-not-reviewed':
      return t('a SNOMED CT code not yet reviewed');
    case 'not-the-condition':
      return t('not the condition itself');
    case 'status-unknown':
      return t('recorded as uncertain');
    case 'hidden':
      return t('hidden from the family');
    default:
      return '';
  }
}

/** One line for a stored result: "10-year risk 12.3%". */
export const qrisk3Summary = (result, t = english) =>
  result && Number.isFinite(Number(result.score))
    ? t('10-year risk {{score}}%', { score: Number(result.score).toFixed(1) })
    : '';
